// Listados → Consumo de Servicios
// Pide un número de socio (titular) y muestra cuántos servicios finalizados
// tuvo por color, mes a mes y por año, desde su primer servicio.
import React, { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import LinearProgress from "@mui/material/LinearProgress";
import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import CloseIcon from "@mui/icons-material/Close";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import Barra from "../Barra";
import Spinner from "../Spinner";
import PantallaHeader from "../common/PantallaHeader";
import clienteAxios from "../../../config/axios";
import { AlertContext } from "../../../context/AlertContext";
import { AuthContext } from "../../../context/AuthContext";
import { ConfiguracionContext } from "../../../context/ConfiguracionContext";
import { ServiciosContext } from "../../../context/ServiciosContext";
import { getBrand, createBrandTheme } from "../../../theme/brand";

// Orden de las columnas de color (solo se muestran los que tuvo el socio)
const ORDEN_COLORES = [
  "ROJO",
  "AMARILLO",
  "VERDE",
  "CELESTE",
  "AZUL",
  "NARANJA",
  "FUCCIA",
  "BLANCO",
  "GRIS",
  "NEGRO",
  "MARRON",
];

const MESES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

export default function ListadosConsumoServicios() {
  const navigate = useNavigate();

  const { mensajeAlerta } = useContext(AlertContext);
  const { state } = useContext(AuthContext);
  const { statusAcceso, configuracion, obtenerAcceso } =
    useContext(ConfiguracionContext);

  // Búsqueda del titular: igual que Listados de Servicios (ID o nombre ->
  // lupa -> "Seleccione el Afiliado"), con obtenerDatosTitulares del contexto
  const { rows, obtenerDatosTitulares, setRows } = useContext(ServiciosContext);
  const [titularId, setTitularId] = useState("");
  const [titularNombre, setTitularNombre] = useState("");
  const [titularSeleccionado, setTitularSeleccionado] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [resultado, setResultado] = useState(null);

  // La lista de titulares es compartida en el contexto: se limpia al entrar
  useEffect(() => {
    setRows(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buscarAfiliados = () => {
    if (titularNombre.trim() !== "") {
      obtenerDatosTitulares({ opcion: "Apellido y nombre", texto: titularNombre.trim() });
    } else if (titularId.trim() !== "") {
      obtenerDatosTitulares({ opcion: "Número de socio", texto: titularId.trim() });
    } else {
      mensajeAlerta(
        "Consumo de Servicios",
        "Ingrese el ID del titular o el apellido y nombre.",
        "warn",
      );
    }
  };

  const theme = createBrandTheme(getBrand(configuracion));

  useEffect(() => {
    if (state.usuario) {
      obtenerAcceso({ usuarioId: state.usuario.id, layout: "Listados" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.id]);

  useEffect(() => {
    if (statusAcceso === 400) {
      mensajeAlerta(
        "Accesos",
        "No tiene permiso para acceder a esta pantalla !.",
        "error",
      );
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusAcceso]);

  // Consumo del titular elegido en "Seleccione el Afiliado"
  const buscar = async (idTitular) => {
    if (!idTitular) return;
    setCargando(true);
    try {
      const respuesta = await clienteAxios.post(
        "/api/servicios/obtenerconsumoserviciostitular",
        { titularId: idTitular },
      );
      setResultado(respuesta.data);
      if (!respuesta.data.filas || respuesta.data.filas.length === 0) {
        mensajeAlerta(
          "Consumo de Servicios",
          "El socio no tiene servicios finalizados.",
          "info",
        );
      }
    } catch (error) {
      setResultado(null);
      mensajeAlerta(
        "Consumo de Servicios",
        (error.response && error.response.data) || "Hubo un error.",
        "error",
      );
    } finally {
      setCargando(false);
    }
  };

  // Arma las tablas mensual y anual a partir de las filas año/mes/color
  const { colores, mensual, anual, total } = useMemo(() => {
    const filas = (resultado && resultado.filas) || [];
    const presentes = new Set(filas.map((f) => f.color));
    const otros = [...presentes].filter((c) => !ORDEN_COLORES.includes(c));
    const colores = [...ORDEN_COLORES.filter((c) => presentes.has(c)), ...otros];

    const porMes = new Map();
    const porAnio = new Map();
    for (const f of filas) {
      const claveMes = `${f.anio}-${String(f.mes).padStart(2, "0")}`;
      if (!porMes.has(claveMes)) porMes.set(claveMes, { anio: f.anio, mes: f.mes, total: 0 });
      if (!porAnio.has(f.anio)) porAnio.set(f.anio, { anio: f.anio, total: 0 });
      const m = porMes.get(claveMes);
      const a = porAnio.get(f.anio);
      m[f.color] = (m[f.color] || 0) + f.cantidad;
      a[f.color] = (a[f.color] || 0) + f.cantidad;
      m.total += f.cantidad;
      a.total += f.cantidad;
    }
    const mensual = [...porMes.entries()].sort(([x], [y]) => x.localeCompare(y)).map(([, v]) => v);
    const anual = [...porAnio.values()].sort((x, y) => x.anio - y.anio);
    const total = anual.reduce((s, a) => s + a.total, 0);
    return { colores, mensual, anual, total };
  }, [resultado]);

  const exportarExcel = () => {
    const filaExcel = (base, fila) => {
      const out = { ...base };
      colores.forEach((c) => {
        out[c] = fila[c] || 0;
      });
      out.TOTAL = fila.total;
      return out;
    };
    const hojaMensual = XLSX.utils.json_to_sheet(
      mensual.map((m) => filaExcel({ AÑO: m.anio, MES: MESES[m.mes - 1] }, m)),
    );
    const hojaAnual = XLSX.utils.json_to_sheet(
      anual.map((a) => filaExcel({ AÑO: a.anio }, a)),
    );
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hojaAnual, "Anual");
    XLSX.utils.book_append_sheet(libro, hojaMensual, "Mensual");
    const buffer = XLSX.write(libro, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      `consumo_servicios_${resultado.titularId}.xlsx`,
    );
  };

  // PDF con el mismo contenido: encabezado del socio, resumen anual y
  // detalle mensual (jspdf + jspdf-autotable, igual que otras pantallas)
  const exportarPdf = () => {
    // Se abre la pestaña en el momento del clic (si se abre después de armar
    // el PDF, el navegador la bloquea como ventana emergente)
    const ventana = window.open("", "_blank");
    import("jspdf").then((jsPDF) => {
      import("jspdf-autotable").then(() => {
        const doc = new jsPDF.default({ orientation: "landscape" });
        const columnas = (conMes) => [
          "Año",
          ...(conMes ? ["Mes"] : []),
          ...colores,
          "Total",
        ];
        const filasPdf = (filas, conMes) =>
          filas.map((f) => [
            f.anio,
            ...(conMes ? [MESES[f.mes - 1]] : []),
            ...colores.map((c) => f[c] || 0),
            f.total,
          ]);

        doc.setFontSize(14);
        doc.text("Consumo de Servicios", 14, 14);
        doc.setFontSize(10);
        doc.text(
          `Socio ${resultado.titularId}${
            resultado.nombreTitular ? " - " + resultado.nombreTitular : ""
          }  ·  ${total} servicios desde ${MESES[mensual[0].mes - 1]} ${mensual[0].anio}`,
          14,
          21,
        );

        doc.autoTable({
          startY: 27,
          head: [columnas(false)],
          body: filasPdf(anual, false),
          styles: { fontSize: 8, halign: "center" },
          headStyles: { fillColor: [71, 85, 105] },
        });
        doc.autoTable({
          startY: doc.lastAutoTable.finalY + 8,
          head: [columnas(true)],
          body: filasPdf(mensual, true),
          styles: { fontSize: 8, halign: "center" },
          headStyles: { fillColor: [71, 85, 105] },
        });

        // Se visualiza en la pestaña nueva (sin descargar)
        const url = doc.output("bloburl");
        if (ventana) {
          ventana.location.href = url;
        } else {
          window.open(url, "_blank");
        }
      });
    });
  };

  const hayDatos = mensual.length > 0;

  const celdaColor = (c) => (
    <TableCell key={c} align="center" sx={{ fontWeight: 700, p: 0.5 }}>
      <Box
        component="span"
        className={c.toLowerCase()}
        sx={{ px: 0.75, py: 0.25, borderRadius: 1, fontSize: 11, display: "inline-block" }}
      >
        {c}
      </Box>
    </TableCell>
  );

  const tabla = (titulo, filas, conMes) => (
    <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, px: 2, pt: 1.5 }}>
        {titulo}
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Año</TableCell>
              {conMes ? <TableCell sx={{ fontWeight: 700 }}>Mes</TableCell> : null}
              {colores.map(celdaColor)}
              <TableCell align="center" sx={{ fontWeight: 700 }}>
                Total
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filas.map((f) => (
              <TableRow key={`${f.anio}-${f.mes || ""}`} hover>
                <TableCell>{f.anio}</TableCell>
                {conMes ? <TableCell>{MESES[f.mes - 1]}</TableCell> : null}
                {colores.map((c) => (
                  <TableCell key={c} align="center">
                    {f[c] || 0}
                  </TableCell>
                ))}
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  {f.total}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  return (
    <Fragment>
      <Barra />
      {statusAcceso !== 200 ? <Spinner /> : null}
      {statusAcceso === 200 ? (
        <ThemeProvider theme={theme}>
          <Box sx={{ px: { xs: 1, sm: 2 }, pb: 3 }}>
            <PantallaHeader
              icono={<QueryStatsOutlinedIcon />}
              titulo="Consumo de Servicios"
              count={hayDatos ? total : null}
              acciones={[
                {
                  label: "Excel",
                  icon: <FileDownloadOutlinedIcon />,
                  color: "success",
                  onClick: exportarExcel,
                  disabled: !hayDatos,
                },
                {
                  label: "PDF",
                  icon: <PictureAsPdfOutlinedIcon />,
                  color: "error",
                  onClick: exportarPdf,
                  disabled: !hayDatos,
                },
                {
                  label: "Cerrar",
                  variant: "text",
                  color: "inherit",
                  icon: <CloseIcon />,
                  onClick: () => navigate("/"),
                },
              ]}
            />

            <Paper
              elevation={0}
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2, mb: 2 }}
            >
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
                <TextField
                  label="ID Titular"
                  size="small"
                  type="number"
                  value={titularId}
                  autoFocus
                  disabled={titularNombre.trim() !== ""}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => {
                    setTitularId(e.target.value);
                    setTitularSeleccionado(null);
                    setRows(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") buscarAfiliados();
                  }}
                  sx={{ minWidth: 160 }}
                />
                <TextField
                  label="Apellido y Nombre o Razón Social"
                  size="small"
                  value={titularNombre}
                  disabled={titularId.trim() !== ""}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => {
                    setTitularNombre(e.target.value);
                    setTitularSeleccionado(null);
                    setRows(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") buscarAfiliados();
                  }}
                  sx={{ minWidth: 280 }}
                />
                <Tooltip title="Buscar titular">
                  <IconButton
                    onClick={buscarAfiliados}
                    sx={{ bgcolor: "success.main", color: "#fff", "&:hover": { bgcolor: "success.dark" } }}
                  >
                    <SearchIcon />
                  </IconButton>
                </Tooltip>
                <Autocomplete
                  options={rows || []}
                  disabled={!rows}
                  noOptionsText="No hay opciones"
                  value={titularSeleccionado}
                  getOptionLabel={(option) =>
                    `${option.id_titular} - ${option.nombre_razonsocial}`
                  }
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  onChange={(event, value) => {
                    setTitularSeleccionado(value || null);
                    if (value) {
                      buscar(value.id_titular);
                    } else {
                      setResultado(null);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Seleccione el Afiliado" />
                  )}
                  sx={{ minWidth: 320, flex: 1 }}
                />
              </Stack>
              {resultado && hayDatos ? (
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 1.5 }}>
                  {`Socio ${resultado.titularId}${
                    resultado.nombreTitular ? " - " + resultado.nombreTitular : ""
                  } · ${total} servicios desde ${MESES[mensual[0].mes - 1]} ${mensual[0].anio}`}
                </Typography>
              ) : null}
              {cargando ? <LinearProgress sx={{ mt: 1.5 }} /> : null}
            </Paper>

            {hayDatos ? (
              <Fragment>
                {tabla("Resumen anual", anual, false)}
                {tabla("Detalle mensual", mensual, true)}
              </Fragment>
            ) : null}
          </Box>
        </ThemeProvider>
      ) : null}
    </Fragment>
  );
}
